import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function usePush() {
  const supported = typeof navigator !== "undefined" &&
    "serviceWorker" in navigator && "PushManager" in window && window.isSecureContext;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    api.get("/push/status").then(({ data }) => setEnabled(data.enabled)).catch(() => {});
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) { toast.error("Your browser does not support push notifications."); return; }
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { toast.error("Notification permission denied."); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      await api.post("/push/subscribe", sub.toJSON());
      setEnabled(true);
      toast.success("Browser notifications enabled");
    } catch (e) {
      toast.error("Could not enable notifications.");
    } finally { setBusy(false); }
  }, [supported]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js") || await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Browser notifications disabled");
    } catch (e) {
      toast.error("Could not disable notifications.");
    } finally { setBusy(false); }
  }, []);

  return { supported, enabled, busy, enable, disable };
}
